// TraceScrubber — horizontal timeline replacement for the vertical
// ActionCard stack in the /ai trace panel.
//
// Why: the verifier's note said the existing per-action-name formatting
// is exhaustive but hidden behind expand clicks. The scrubber turns that
// hidden craft into the primary affordance — visitors drag through the
// agent's pipeline at their own pace and see the same query's state at
// every step.
//
// Layout: dots stacked horizontally with status colour + duration. The
// selected dot's full ActionCard renders below. Auto-follows the latest
// action while streaming, unless the visitor has manually selected
// something else (then it stays put). Keyboard arrows step through.
//
// Mobile (<768px): falls back to the vertical stack entirely. Horizontal
// scrub in a phone-width rail is a UX disaster.

import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";

export type ScrubberAction = {
  id: string;
  name: string;
  status: "running" | "ok" | "failed";
  durationMs?: number;
};

function useIsWide(threshold = 768) {
  const [wide, setWide] = useState(() =>
    typeof window === "undefined" ? true : window.innerWidth >= threshold,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${threshold}px)`);
    const onChange = (e: MediaQueryListEvent) => setWide(e.matches);
    setWide(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [threshold]);
  return wide;
}

type TraceScrubberProps<A extends ScrubberAction> = {
  actions: A[];
  renderCard: (a: A) => React.ReactNode;
  // When defined, the fallback (mobile) view stacks rendered cards
  // top-to-bottom. We re-use the same renderer for both layouts.
};

export function TraceScrubber<A extends ScrubberAction>({
  actions,
  renderCard,
}: TraceScrubberProps<A>) {
  const isWide = useIsWide();

  // Selected index in `actions`. `null` means "follow the latest".
  const [pinned, setPinned] = useState<number | null>(null);
  const selectedIdx =
    pinned !== null && pinned >= 0 && pinned < actions.length
      ? pinned
      : Math.max(0, actions.length - 1);

  // Whenever the action list grows, scroll the strip to keep the latest
  // visible. Don't move the pin — if the user pinned, leave them there.
  const stripRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!stripRef.current) return;
    stripRef.current.scrollTo({
      left: stripRef.current.scrollWidth,
      behavior: "smooth",
    });
  }, [actions.length]);

  // Keyboard nav when the scrubber container has focus.
  const onKey = (e: React.KeyboardEvent) => {
    if (actions.length === 0) return;
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setPinned(Math.max(0, selectedIdx - 1));
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      const next = Math.min(actions.length - 1, selectedIdx + 1);
      setPinned(next === actions.length - 1 ? null : next);
    } else if (e.key === "End") {
      e.preventDefault();
      setPinned(null);
    } else if (e.key === "Home") {
      e.preventDefault();
      setPinned(0);
    }
  };

  const headline = useMemo(() => {
    if (actions.length === 0) return null;
    const totalMs = actions.reduce((sum, a) => sum + (a.durationMs ?? 0), 0);
    const okCount = actions.filter((a) => a.status === "ok").length;
    const failCount = actions.filter((a) => a.status === "failed").length;
    return { totalMs, okCount, failCount };
  }, [actions]);

  if (actions.length === 0) return null;

  // ── Mobile / narrow: vertical stack of the rendered cards.
  if (!isWide) {
    return (
      <div className="space-y-2">
        {actions.map((a) => (
          <div key={a.id}>{renderCard(a)}</div>
        ))}
      </div>
    );
  }

  const selectedAction = actions[selectedIdx];

  return (
    <div
      tabIndex={0}
      onKeyDown={onKey}
      className="space-y-2 focus:outline-none"
      aria-label="Trace scrubber"
    >
      {/* Header — counts + scrub position */}
      <div className="flex items-center justify-between text-[10.5px] uppercase tracking-wider text-stone-400">
        <span>
          step {selectedIdx + 1}/{actions.length}
        </span>
        {headline && (
          <span>
            <span className="text-emerald-600">{headline.okCount} ✓</span>
            {headline.failCount > 0 && (
              <span className="ml-1 text-rose-600">{headline.failCount} ✗</span>
            )}
            <span className="ml-1 tabular-nums">· {Math.round(headline.totalMs)}ms</span>
          </span>
        )}
      </div>

      {/* Horizontal dot strip */}
      <div
        ref={stripRef}
        className="relative flex items-center gap-1 overflow-x-auto pb-1 pt-0.5 [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-stone-300"
      >
        {/* Backing rail */}
        <div className="pointer-events-none absolute inset-x-0 top-1/2 h-px bg-stone-200" />
        {actions.map((a, i) => {
          const isSelected = i === selectedIdx;
          const dotTone =
            a.status === "ok"
              ? "bg-emerald-500"
              : a.status === "failed"
                ? "bg-rose-500"
                : "bg-amber-500 animate-pulse";
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => setPinned(i === actions.length - 1 ? null : i)}
              title={`${a.name}${a.durationMs ? ` · ${a.durationMs}ms` : ""}`}
              className={`group relative z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition ${
                isSelected ? "scale-125" : "hover:scale-110"
              }`}
              aria-label={`Step ${i + 1}: ${a.name}`}
              aria-current={isSelected ? "step" : undefined}
            >
              <span className={`h-2 w-2 rounded-full ${dotTone}`} />
              {isSelected && (
                <motion.span
                  layoutId="scrub-ring"
                  className="absolute inset-0 rounded-full border border-stone-900"
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Selected card */}
      <motion.div
        key={selectedAction.id}
        initial={{ opacity: 0, y: 2 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18 }}
      >
        {renderCard(selectedAction)}
      </motion.div>

      {/* Hint — only visible when more than one step exists */}
      {actions.length > 1 && (
        <p className="pt-0.5 text-center text-[9.5px] uppercase tracking-wider text-stone-300">
          ← → to scrub · End for live
        </p>
      )}
    </div>
  );
}
